import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AppService } from '../../core/app.service';
import { IconComponent } from '../../shared/icon/icon.component';
import { TagComponent } from '../../shared/widgets/tag/tag.component';
import { DrawerComponent } from '../../shared/widgets/drawer/drawer.component';
import { PAGE_HELP_LABEL, PageHelp, TabHelp, helpSectionFor, HelpSectionName, resolvePageHelp } from './network-help.data';

export interface NetworkHelpDrawerData { key: string; subKey?: string | null; }

/**
 * Contextual "Page help" slide-out for the NETWORK and SYSTEM sections — a port
 * of the design's help drawer. Each page is explained on two axes (business /
 * software) with quick links to related pages. Opened from the topbar Help
 * button on any route that has a help entry.
 *
 * Content is language-aware: {@link resolvePageHelp} returns the localized
 * entry for the app's current language when one exists (e.g. Persian / French
 * for Markup & Rules), otherwise the English default. The drawer is rebuilt on
 * every open, so it always reflects the language selected at that moment.
 */
@Component({
  selector: 'app-network-help-drawer',
  standalone: true,
  imports: [IconComponent, TagComponent, DrawerComponent, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './network-help-drawer.component.html',
  styleUrl: './network-help-drawer.component.scss',
})
export class NetworkHelpDrawerComponent {
  private readonly dialogRef = inject(MatDialogRef<NetworkHelpDrawerComponent>);
  private readonly router = inject(Router);
  private readonly app = inject(AppService);
  private readonly data = inject<NetworkHelpDrawerData>(MAT_DIALOG_DATA);

  readonly help: PageHelp | undefined = resolvePageHelp(this.data.key, this.app.lang());
  readonly section: HelpSectionName = helpSectionFor(this.data.key);
  /** ngx-translate key for the section chip — e.g. 'help.sections.Network'. */
  readonly sectionKey = `help.sections.${this.section}`;

  /** The focused tab's help, when the page has per-tab help and the opening
   *  screen published an active tab (subKey) that matches one. Rendered as a
   *  highlighted band above the shared page help. */
  readonly tabHelp: TabHelp | undefined =
    this.data.subKey ? this.help?.tabs?.[this.data.subKey] : undefined;

  label(key: string): string { return PAGE_HELP_LABEL[key] ?? key; }

  close(): void { this.dialogRef.close(); }

  /** Related-page link: close, then navigate to that route. */
  go(key: string): void {
    this.dialogRef.close();
    this.router.navigate([`/${key}`]);
  }
}
